import React, { useState, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { fetchCES4Data, CES4_INDICATOR_MAP, CES4Data } from '../../services/ces4DataService';
import { supabaseClient } from '../../services/supabase';
import { fetchFossilHeatingBurdenPercentile } from '../../services/fossilHeatingPercentileService';
import { displayPercentile } from '../../utils/percentileUtils';
import './ChartLayout.css';

Chart.register(...registerables);

interface BurdenBenefitsChartProps {
  type: 'burdens' | 'benefits';
  housingBurdenPercentile?: number | null;
  housingBurdenRenterPercentile?: number | null;
  housingBurdenOwnerPercentile?: number | null;
  /** Same source as Livability Over Time (livability_index_precalculated); overrides client fetch when set */
  fossilHeatingLivabilityPercentile?: number | null;
  onPercentileCalculated?: (percentile: number | null) => void;
}

interface Category {
  name: string;
  percentile: number;
  expanded: boolean;
  indicators: {
    name: string;
    percentile: number | null; // null means "coming soon"
  }[];
}

type HeatingFuelRow = {
  location_name: string;
  geography_type: string;
  year: number;
  house_heating_fuel: string;
  percent_hh?: number | string | null;
  percent_of_households?: number | string | null;
};

export const BurdenBenefitsChart: React.FC<BurdenBenefitsChartProps> = ({
  type,
  housingBurdenPercentile,
  housingBurdenRenterPercentile,
  housingBurdenOwnerPercentile,
  fossilHeatingLivabilityPercentile,
  onPercentileCalculated,
}) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Log when selectedGeofips changes to track potential issues
  useEffect(() => {
    if (selectedLocation && selectedGeofips) {
      console.log(`🔍 BurdenBenefitsChart - selectedGeofips changed:`, {
        location: selectedLocation,
        geofips: selectedGeofips,
        timestamp: new Date().toISOString()
      });
    }
  }, [selectedLocation, selectedGeofips]);
  const [ces4Data, setCes4Data] = useState<CES4Data | null>(null);
  const [ghgPercentiles, setGhgPercentiles] = useState<{
    ldv?: number;
    hdv?: number;
    electricity?: number;
    naturalGas?: number;
    waste?: number;
    offroad?: number;
    total?: number;
  } | null>(null);
  const [commuteTimePercentile, setCommuteTimePercentile] = useState<number | null>(null);
  const [evPer1000Percentile, setEvPer1000Percentile] = useState<number | null>(null);
  const [evChargingPercentile, setEvChargingPercentile] = useState<number | null>(null);
  const [solarPVPercentile, setSolarPVPercentile] = useState<number | null>(null);
  const [heatPumpPercentile, setHeatPumpPercentile] = useState<number | null>(null);
  const [treeCanopyPercentile, setTreeCanopyPercentile] = useState<number | null>(null);
  const [displacementRiskPercentile, setDisplacementRiskPercentile] = useState<number | null>(null);
  const [percentFossilHeatingPercentile, setPercentFossilHeatingPercentile] = useState<number | null>(null);
  const [urbanHeatIslandPercentile, setUrbanHeatIslandPercentile] = useState<number | null>(null);
  const [crimeSafetyRiskPercentile, setCrimeSafetyRiskPercentile] = useState<number | null>(null);
  const [fossilHeatingPercentile, setFossilHeatingPercentile] = useState<number | null>(null);
  const [educationalAttainmentPercentile, setEducationalAttainmentPercentile] = useState<number | null>(null);
  const [percentBachelorsRaw, setPercentBachelorsRaw] = useState<number | null>(null);
  const [healthInsurancePercentile, setHealthInsurancePercentile] = useState<number | null>(null);
  const [medianIncomePercentile, setMedianIncomePercentile] = useState<number | null>(null);
  const [jobWagePercentile, setJobWagePercentile] = useState<number | null>(null);
  const [equityPercentiles, setEquityPercentiles] = useState<{
    incomeEquity?: number;
    educationEquity?: number;
    wageEquity?: number;
  } | null>(null);

  // Fetch CES4 data when location changes
  useEffect(() => {
    const loadCES4Data = async () => {
      if (!selectedLocation) {
        setCes4Data(null);
        return;
      }

      try {
        const data = await fetchCES4Data(selectedLocation);
        setCes4Data(data);
      } catch (error) {
        console.error('Error loading CES4 data:', error);
        setCes4Data(null);
      }
    };

    loadCES4Data();
  }, [selectedLocation]);

  const buildGeofipsVariants = (gf: number | string): string[] => {
    const str = String(gf);
    const variants = [str];
    if (str.length === 6 || str.length === 4 || str.length === 1) variants.push('0' + str);
    const unpadded = str.replace(/^0+/, '') || '0';
    if (!variants.includes(unpadded)) variants.push(unpadded);
    return variants;
  };

  const fetchSimplePercentile = async (
    table: string, percentileCol: string, geofipsCol: string, geofips: number | string, year = 2023
  ): Promise<number | null> => {
    const { data, error } = await supabaseClient
      .from(table).select(`year, ${percentileCol}`)
      .eq(geofipsCol, geofips).eq('year', year)
      .not(percentileCol, 'is', null).limit(1);
    if (error) { console.error(`Error loading ${table} percentile:`, error); return null; }
    return data?.[0] ? displayPercentile((data[0] as any)[percentileCol]) : null;
  };

  const fetchWithGeofipsVariants = async (
    table: string, percentileCol: string, geofips: number | string, location: string | null
  ): Promise<number | null> => {
    const variants = buildGeofipsVariants(geofips);
    // urban_heat_island_place has no year column (see sql/create_urban_heat_island_place.sql); ordering by year → PostgREST 400.
    const orderByYear = table !== 'urban_heat_island_place';
    let q = supabaseClient.from(table).select(percentileCol).in('geofips', variants).not(percentileCol, 'is', null);
    if (orderByYear) q = q.order('year', { ascending: false });
    const result = await q.limit(1);
    if (result.data?.[0]) {
      const val = (result.data[0] as any)[percentileCol];
      return val != null && Number.isFinite(Number(val)) ? Math.round(Number(val) <= 1 ? Number(val) * 100 : Number(val)) : null;
    }
    if (location) {
      const fallback = await supabaseClient.from(table).select(percentileCol)
        .ilike('location_name', `%${location}%`).not(percentileCol, 'is', null).limit(1);
      if (fallback.data?.[0]) {
        const val = (fallback.data[0] as any)[percentileCol];
        return val != null && Number.isFinite(Number(val)) ? Math.round(Number(val) <= 1 ? Number(val) * 100 : Number(val)) : null;
      }
    }
    return null;
  };

  const fetchWithYearFallback = async (
    table: string, percentileCol: string, geofipsCol: string, geofips: number | string,
    location: string | null, countyFallback = false
  ): Promise<number | null> => {
    const gfVal = typeof geofips === 'string' ? geofips : Number(geofips);
    const res2023 = await supabaseClient.from(table).select(`year, ${percentileCol}`)
      .eq(geofipsCol, gfVal).eq('year', 2023).not(percentileCol, 'is', null).limit(1);
    if (res2023.data?.[0]) return displayPercentile((res2023.data[0] as any)[percentileCol]);
    const resLatest = await supabaseClient.from(table).select(`year, ${percentileCol}`)
      .eq(geofipsCol, gfVal).not(percentileCol, 'is', null)
      .order('year', { ascending: false }).limit(1);
    if (resLatest.data?.[0]) return displayPercentile((resLatest.data[0] as any)[percentileCol]);
    if (countyFallback && location) {
      const countyName = supabaseLocationService.getCountyForCity(location);
      if (countyName) {
        const c2023 = await supabaseClient.from(table).select(`year, ${percentileCol}`)
          .eq('location_name', countyName).eq('geography_type', 'county').eq('year', 2023)
          .not(percentileCol, 'is', null).limit(1);
        if (c2023.data?.[0]) return displayPercentile((c2023.data[0] as any)[percentileCol]);
        const cLatest = await supabaseClient.from(table).select(`year, ${percentileCol}`)
          .eq('location_name', countyName).eq('geography_type', 'county')
          .not(percentileCol, 'is', null).order('year', { ascending: false }).limit(1);
        if (cLatest.data?.[0]) return displayPercentile((cLatest.data[0] as any)[percentileCol]);
      }
    }
    return null;
  };

  // Consolidated percentile data fetch
  useEffect(() => {
    const loadAllPercentiles = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setGhgPercentiles(null); setCommuteTimePercentile(null); setEvPer1000Percentile(null);
        setEvChargingPercentile(null); setSolarPVPercentile(null); setHeatPumpPercentile(null);
        setEducationalAttainmentPercentile(null); setPercentBachelorsRaw(null); setTreeCanopyPercentile(null);
        setUrbanHeatIslandPercentile(null); setCrimeSafetyRiskPercentile(null); setDisplacementRiskPercentile(null);
        setEquityPercentiles(null); setMedianIncomePercentile(null); setJobWagePercentile(null);
        setHealthInsurancePercentile(null);
        return;
      }

      const geofipsInt = Number(selectedGeofips);

      const [ghgResult, commuteResult, evResult, evChargingResult, solarResult, heatPumpResult,
        eduResult, treeResult, uhiResult, crimeResult, displacementResult, fossilHeatingResult, equityResult,
        healthInsResult, bachelorsRaceResult] = await Promise.allSettled([
        // GHG percentiles
        supabaseClient.from('ghg_inventory_precalculated')
          .select('ldv_emissions_percentile, hdv_emissions_percentile, elec_total_emissions_percentile, ng_total_emissions_percentile, waste_emissions_percentile, offroad_emissions_percentile, total_emissions_percentile, ev_per_1000_people_percentile')
          .eq('geofips', selectedGeofips).eq('year', 2023).limit(1),
        // Commute time
        fetchSimplePercentile('commute_time', 'percentile', 'geofips', String(selectedGeofips)),
        // EV per 1000 — already fetched via GHG query above, handled in processing
        Promise.resolve(null),
        // EV Charging
        (() => {
          const str = String(selectedGeofips);
          const geofipsNum = parseInt(str.replace(/^0+/, '') || str, 10);
          const tryGf: (string | number)[] = [selectedGeofips];
          if (!Number.isNaN(geofipsNum) && geofipsNum > 0) tryGf.push(geofipsNum);
          return (async () => {
            for (const gf of tryGf) {
              const res2023 = await supabaseClient.from('ev_chargers')
                .select('year, chargers_per_1000_residents_percentile')
                .eq('geofips', gf).eq('year', 2023)
                .not('chargers_per_1000_residents_percentile', 'is', null).limit(1);
              if (res2023.data?.length) return displayPercentile(res2023.data[0].chargers_per_1000_residents_percentile);
              const resLatest = await supabaseClient.from('ev_chargers')
                .select('year, chargers_per_1000_residents_percentile')
                .eq('geofips', gf).not('chargers_per_1000_residents_percentile', 'is', null)
                .order('year', { ascending: false }).limit(1);
              if (resLatest.data?.length) return displayPercentile(resLatest.data[0].chargers_per_1000_residents_percentile);
            }
            return null;
          })();
        })(),
        // Solar PV
        fetchSimplePercentile('solar_pv_per_capita', 'capacity_per_capita_percentile', 'geoid', selectedGeofips),
        // Heat Pump
        fetchSimplePercentile('heat_pump_per_capita', 'units_per_10000_percentile', 'geoid', selectedGeofips),
        // Educational Attainment
        fetchSimplePercentile('educational_attainment_percentiles', 'percent_bachelors_percentile', 'geofips', selectedGeofips),
        // Tree Canopy
        Number.isFinite(geofipsInt) ? fetchWithYearFallback('tree_canopy_coverage', 'percentile', 'geofips', geofipsInt, selectedLocation, true) : Promise.resolve(null),
        // Urban Heat Island
        fetchWithGeofipsVariants('urban_heat_island_place', 'uhii_percentile', selectedGeofips, selectedLocation),
        // Crime Safety
        fetchWithGeofipsVariants('crime_safety_place', 'crime_safety_risk_percentile', selectedGeofips, selectedLocation),
        // Displacement Risk
        Number.isFinite(geofipsInt) ? fetchWithYearFallback('displacement_risk', 'percentile', 'geofips', geofipsInt, selectedLocation, true) : Promise.resolve(null),
        // Percent Fossil Heating
        (async () => {
          const { data, error } = await supabaseClient
            .from('house_heating_fuel')
            .select('location_name, geography_type, year, house_heating_fuel, percent_hh');
          let rows = (data || []) as HeatingFuelRow[];
          if (error && String(error.message || '').includes('percent_hh')) {
            const legacy = await supabaseClient
              .from('house_heating_fuel')
              .select('location_name, geography_type, year, house_heating_fuel, percent_of_households');
            rows = (legacy.data || []) as HeatingFuelRow[];
          }
          if (!rows.length) return null;
          const fossilFuels = new Set(['utility gas', 'bottled, tank, or lp gas', 'fuel oil, kerosene, etc.', 'coal or coke', 'other fuel']);
          const valueOf = (r: HeatingFuelRow) => {
            const raw = Number((r.percent_hh ?? r.percent_of_households) as any);
            return Number.isFinite(raw) ? raw : null;
          };
          const selectedRows = rows.filter((r) => String(r.location_name || '').toLowerCase() === selectedLocation.toLowerCase());
          const selectedYears = Array.from(new Set(selectedRows.map((r) => Number(r.year)).filter((y) => Number.isFinite(y))));
          if (!selectedYears.length) return null;
          const targetYear = selectedYears.includes(2023) ? 2023 : Math.max(...selectedYears);
          const locType = supabaseLocationService.getLocationType(selectedLocation);
          const peerGeographyType = locType === 'county' ? 'county' : locType === 'city' ? 'place' : null;
          const computeShare = (name: string): number | null => {
            const yrRows = rows.filter((r) => Number(r.year) === targetYear && String(r.location_name || '').toLowerCase() === name.toLowerCase());
            if (!yrRows.length) return null;
            let total = 0, fossil = 0;
            yrRows.forEach((r) => {
              const v = valueOf(r);
              if (v == null) return;
              total += v;
              if (fossilFuels.has(String(r.house_heating_fuel || '').toLowerCase())) fossil += v;
            });
            return total > 0 ? (fossil / total) * 100 : null;
          };
          const selectedShare = computeShare(selectedLocation);
          if (selectedShare == null) return null;
          const peers = Array.from(new Set(
            rows
              .filter((r) => Number(r.year) === targetYear)
              .filter((r) => (peerGeographyType ? r.geography_type === peerGeographyType : true))
              .map((r) => String(r.location_name || ''))
              .filter(Boolean)
          ));
          const peerShares = peers.map((p) => computeShare(p)).filter((v): v is number => v != null && Number.isFinite(v));
          if (!peerShares.length) return null;
          const lessOrEqual = peerShares.filter((v) => v <= selectedShare).length;
          return displayPercentile((lessOrEqual / peerShares.length) * 100);
        })(),
        // Equity Indicators
        (async () => {
          if (!selectedGeofips || selectedGeofips === 0) return null;
          const locationLower = selectedLocation.toLowerCase();
          if (selectedGeofips === 606000 && !locationLower.includes('berkeley')) return null;
          let geofipsStr = String(selectedGeofips);
          if (/^\d+$/.test(geofipsStr)) {
            if (geofipsStr.length === 6 || geofipsStr.length === 4 || geofipsStr.length === 1) geofipsStr = '0' + geofipsStr;
          }
          const variants = [geofipsStr];
          const unpadded = geofipsStr.replace(/^0+/, '') || '0';
          if (unpadded !== geofipsStr) variants.push(unpadded);
          const { data: rows, error } = await supabaseClient.from('equity_indicators_summary')
            .select('year, income_equity_percentile, education_equity_percentile, wage_equity_percentile, median_household_income_percentile, job_wage_percentile')
            .in('geofips', variants).order('year', { ascending: false }).limit(10);
          if (error) { console.error('Error loading Equity percentiles:', error); return null; }
          const hasEquityData = (row: any) => row?.income_equity_percentile != null || row?.education_equity_percentile != null || row?.wage_equity_percentile != null;
          return rows?.length ? (rows.find(hasEquityData) || rows[0]) : null;
        })(),
        // Health Insurance
        fetchSimplePercentile('health_insurance_percentiles', 'percent_with_insurance_percentile', 'geofips', selectedGeofips),
        // Bachelor's raw % for narrative tooltips
        supabaseClient.from('educational_attain_race').select('percent, count')
          .eq('location_name', selectedLocation).eq('year', 2023)
          .eq('educ_level', "Bachelor's degree or higher").eq('gender', 'Total'),
      ]);

      // Process GHG + EV per 1000
      if (ghgResult.status === 'fulfilled' && ghgResult.value.data?.[0]) {
        const r = ghgResult.value.data[0];
        setGhgPercentiles({
          ldv: displayPercentile(r.ldv_emissions_percentile) ?? undefined,
          hdv: displayPercentile(r.hdv_emissions_percentile) ?? undefined,
          electricity: displayPercentile(r.elec_total_emissions_percentile) ?? undefined,
          naturalGas: displayPercentile(r.ng_total_emissions_percentile) ?? undefined,
          waste: displayPercentile(r.waste_emissions_percentile) ?? undefined,
          offroad: displayPercentile(r.offroad_emissions_percentile) ?? undefined,
          total: displayPercentile(r.total_emissions_percentile) ?? undefined,
        });
        setEvPer1000Percentile(displayPercentile(r.ev_per_1000_people_percentile));
      } else {
        setGhgPercentiles(null); setEvPer1000Percentile(null);
      }

      setCommuteTimePercentile(commuteResult.status === 'fulfilled' ? commuteResult.value : null);
      setEvChargingPercentile(evChargingResult.status === 'fulfilled' ? evChargingResult.value : null);
      setSolarPVPercentile(solarResult.status === 'fulfilled' ? solarResult.value : null);
      setHeatPumpPercentile(heatPumpResult.status === 'fulfilled' ? heatPumpResult.value : null);
      setEducationalAttainmentPercentile(eduResult.status === 'fulfilled' ? eduResult.value : null);
      setTreeCanopyPercentile(treeResult.status === 'fulfilled' ? treeResult.value : null);
      setUrbanHeatIslandPercentile(uhiResult.status === 'fulfilled' ? uhiResult.value : null);
      setCrimeSafetyRiskPercentile(crimeResult.status === 'fulfilled' ? crimeResult.value : null);
      setDisplacementRiskPercentile(displacementResult.status === 'fulfilled' ? displacementResult.value : null);
      setPercentFossilHeatingPercentile(fossilHeatingResult.status === 'fulfilled' ? fossilHeatingResult.value : null);
      setHealthInsurancePercentile(healthInsResult.status === 'fulfilled' ? healthInsResult.value : null);

      // Process equity
      if (equityResult.status === 'fulfilled' && equityResult.value) {
        const latest = equityResult.value;
        setEquityPercentiles({
          incomeEquity: displayPercentile(latest.income_equity_percentile) ?? undefined,
          educationEquity: displayPercentile(latest.education_equity_percentile) ?? undefined,
          wageEquity: displayPercentile(latest.wage_equity_percentile) ?? undefined,
        });
        setMedianIncomePercentile(displayPercentile(latest.median_household_income_percentile));
        setJobWagePercentile(displayPercentile(latest.job_wage_percentile));
      } else {
        setEquityPercentiles(null); setMedianIncomePercentile(null); setJobWagePercentile(null);
      }

      // Process bachelor's raw
      if (bachelorsRaceResult.status === 'fulfilled' && bachelorsRaceResult.value.data?.length) {
        let sumPctCount = 0, sumCount = 0;
        bachelorsRaceResult.value.data.forEach((r: any) => {
          const pct = Number(r.percent) ?? 0, cnt = Number(r.count) ?? 0;
          sumPctCount += pct * (cnt || 1); sumCount += cnt || 1;
        });
        const rawPct = sumCount > 0 ? (sumPctCount / sumCount) * 100 : null;
        setPercentBachelorsRaw(Number.isFinite(rawPct) ? rawPct : null);
      } else {
        setPercentBachelorsRaw(null);
      }
    };

    loadAllPercentiles();
  }, [selectedLocation, selectedGeofips]);

  // When Overview does not pass livability-derived fossil heating, fill from precalculated table then peer fallback.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedLocation) {
        setFossilHeatingPercentile(null);
        return;
      }
      if (fossilHeatingLivabilityPercentile != null && Number.isFinite(fossilHeatingLivabilityPercentile)) {
        setFossilHeatingPercentile(null);
        return;
      }
      const toDisplay = (raw: unknown): number | null => {
        if (raw == null || !Number.isFinite(Number(raw))) return null;
        const n = Number(raw);
        return Math.round(n <= 1 ? n * 100 : n);
      };
      try {
        const geo = await supabaseLocationService.getGeofipsWithGeographyType(selectedLocation);
        const geoTypes = geo
          ? Array.from(new Set([geo.geography_type, 'City', 'Place'].filter(Boolean)))
          : ['City', 'Place'];
        if (!geo) {
          const fb = await fetchFossilHeatingBurdenPercentile(selectedLocation, selectedGeofips, null);
          if (!cancelled) setFossilHeatingPercentile(fb);
          return;
        }
        const loc = selectedLocation.trim();
        for (const y of [2023, 2022, 2021, 2020]) {
          if (cancelled) return;
          for (const gt of geoTypes) {
            if (cancelled) return;
            for (const usePrefix of [false, true]) {
              let q = supabaseClient
                .from('livability_index_precalculated')
                .select('percent_fossil_heating_percentile')
                .eq('geography_type', gt)
                .eq('year', y)
                .limit(1);
              q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
              const { data, error } = await q;
              if (error) continue;
              const p = toDisplay(data?.[0]?.percent_fossil_heating_percentile);
              if (p != null) {
                if (!cancelled) setFossilHeatingPercentile(p);
                return;
              }
            }
          }
        }
        const fb = await fetchFossilHeatingBurdenPercentile(selectedLocation, selectedGeofips, geo.geography_type);
        if (!cancelled) setFossilHeatingPercentile(fb);
      } catch (e) {
        console.error('Error loading fossil heating burden percentile:', e);
        try {
          const fb = await fetchFossilHeatingBurdenPercentile(selectedLocation, selectedGeofips, null);
          if (!cancelled) setFossilHeatingPercentile(fb);
        } catch {
          if (!cancelled) setFossilHeatingPercentile(null);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, selectedGeofips, fossilHeatingLivabilityPercentile]);

  // Helper function to calculate average percentile from indicators
  // Returns 50 if no data exists for any subcategories
  const calculateCategoryPercentile = (indicators: { name: string; percentile: number | null }[]): number => {
    if (indicators.length === 0) return 50;
    const validIndicators = indicators.filter(ind => ind.percentile !== null);
    if (validIndicators.length === 0) return 50;
    const sum = validIndicators.reduce((acc, ind) => acc + (ind.percentile || 0), 0);
    const avg = sum / validIndicators.length;
    return displayPercentile(avg) ?? 50;
  };

  // Helper function to get percentile for an indicator, using CES4 data if available
  const getIndicatorPercentile = (indicatorName: string, fallback: number): number => {
    if (!ces4Data) return fallback;
    
    const ces4Field = CES4_INDICATOR_MAP[indicatorName];
    if (ces4Field && ces4Data[ces4Field] !== null && ces4Data[ces4Field] !== undefined) {
      return displayPercentile(ces4Data[ces4Field] as number) ?? fallback;
    }
    
    return fallback;
  };

  const CATEGORY_INFO: Record<'burdens' | 'benefits', Record<string, string>> = {
    burdens: {
      'Greenhouse Gas Emissions (per capita)': 'Per-capita emissions from transportation, energy use, and waste.',
      Pollution: 'Air and toxic pollution exposures that affect community health.',
      Health: 'Public health outcomes linked to environmental and social conditions.',
      Transportation: 'Travel burdens and traffic exposure from transportation systems.',
      'Built Environment': 'Housing, energy cost burden, heat exposure, and neighborhood conditions.'
    },
    benefits: {
      Equity: 'Equity outcomes across income, education, and wages by race.',
      Socioeconomic: 'Economic stability and access to basic resources.',
      Mobility: 'Access to clean transportation options and infrastructure.',
      'Built Environment': 'Clean building technologies and affordable housing access.',
      'Community Resources': 'Access to green space, public safety, and community amenities.'
    }
  };

  /** Ordinal suffix: 1st, 2nd, 3rd, 4th, 21st, etc. */
  const ordinal = (n: number): string => {
    const s = n % 10, t = n % 100;
    if (t >= 11 && t <= 13) return `${n}th`;
    if (s === 1) return `${n}st`;
    if (s === 2) return `${n}nd`;
    if (s === 3) return `${n}rd`;
    return `${n}th`;
  };

  /** Per-indicator calculation methods for tooltip explanation. Cities ranked among cities; counties among counties. */
  const INDICATOR_CALCULATION: Record<string, string> = {
    'Light-Duty Vehicles (LDV)': 'LDV emissions per capita; percentile among California cities (cities vs cities) or counties (counties vs counties).',
    'Heavy-Duty Vehicles (HDV)': 'HDV emissions per capita; percentile among California cities (cities vs cities) or counties (counties vs counties).',
    Electricity: 'Electricity emissions per capita; percentile among California cities or counties.',
    'Natural Gas': 'Natural gas emissions per capita; percentile among California cities or counties.',
    'Percent Fossil Heating': 'SUM(Utility Gas + Bottled/LP gas + Fuel oil/kerosene + Coal/coke + Other fuel) / total heating-fuel households for the selected year, converted to a percentile among California peer geographies.',
    Waste: 'Waste emissions per capita; percentile among California cities or counties.',
    Offroad: 'Offroad emissions per capita; percentile among California cities or counties.',
    'Diesel PM': 'CalEnviroScreen 4.0 diesel PM percentile; higher = greater exposure.',
    'PM2.5': 'CalEnviroScreen 4.0 PM2.5 percentile.',
    Ozone: 'CalEnviroScreen 4.0 ozone percentile.',
    'Toxic Releases': 'CalEnviroScreen 4.0 toxic release percentile.',
    'Hazardous Waste': 'CalEnviroScreen 4.0 hazardous waste percentile.',
    'Asthma Rate': 'CalEnviroScreen 4.0 asthma percentile; higher = greater burden.',
    'Cardiovascular Disease': 'CalEnviroScreen 4.0 cardiovascular disease percentile.',
    'Low Birth Weight': 'CalEnviroScreen 4.0 low birth weight percentile.',
    'Commute Time': 'ACS commute data; percentile among California cities or counties.',
    Traffic: 'CalEnviroScreen 4.0 traffic percentile.',
    'Renter Burden': 'ACS housing burden data; renters with 35%+ income spent on housing; percentile among California cities or counties.',
    'Owner Burden': 'ACS housing burden data; owners with 35%+ income spent on housing; percentile among California cities or counties.',
    'Displacement Risk': 'Low-income households in areas with declining low-income population; proportion at risk scaled to percentile among California cities or counties.',
    'Income Equity by Race': 'Ratio of lowest to highest median income across racial/ethnic groups; equity score from ratios; percentile among California cities or counties.',
    'Educational Attainment Equity by Race': 'Ratio of lowest to highest bachelor\'s+ rate across racial/ethnic groups; equity score from ratios; percentile among California cities or counties.',
    'Wage Equity by Sector': 'Ratio of lowest to highest sector wages by race; equity score from ratios; percentile among California cities or counties.',
    'Median Household Income': 'ACS median income; percentile among California cities or counties.',
    'Educational Attainment': 'ACS educational attainment (adults 25+ with bachelor\'s+); percentile among California cities or counties.',
    'Health Insurance Coverage': 'ACS Table B27001; percent uninsured, inverted to benefits scale; percentile among California cities or counties.',
    'Job Wages': 'ACS sector wages; percentile among California cities or counties.',
    "EV's per 1000 People": 'EMFAC/DMV fleet data; EVs (BEV+PHEV) per 1,000 people; percentile among California cities or counties.',
    'EV Charging': 'AFDC chargers + population; people per charger; percentile among California cities or counties.',
    'Solar PV': 'CPUC/NEM data; solar MW per capita; percentile among California cities or counties.',
    'Heat Pump Adoption': 'Building permit data; percentile among California cities or counties.',
    'Tree Canopy Coverage': 'Urban tree canopy data; percentile among California cities or counties.',
    'Public Safety': 'Violent crime risk percentile (CDPH Healthy Communities / place-level data); inverted so higher percentiles = lower crime risk vs peers.'
  };

  /** Resolve "among cities" vs "among counties" based on location type. */
  const peerPhrase = ((): string => {
    const locType = selectedLocation ? supabaseLocationService.getLocationType(selectedLocation) : null;
    if (locType === 'county') return 'among California counties';
    if (locType === 'city') return 'among California cities';
    return 'among California cities or counties';
  })();

  /** Build smart narrative tooltip: contextual sentence with location, value, percentile; no "under X" phrase; includes calculation. */
  const getSmartNarrative = (
    indicatorName: string,
    _categoryName: string,
    percentile: number | null,
    locationName: string | null
  ): string => {
    const loc = locationName || 'This area';
    const pct = displayPercentile(percentile);
    let calc = INDICATOR_CALCULATION[indicatorName];
    if (calc) {
      calc = calc
        .replace(/percentile among California cities or counties\.?/gi, `percentile ${peerPhrase}.`)
        .replace(/percentile among California cities \(cities vs cities\) or counties \(counties vs counties\)\.?/gi, `percentile ${peerPhrase}.`);
    }

    const narrative = (() => {
      if (indicatorName === 'Educational Attainment' && percentBachelorsRaw != null && Number.isFinite(percentBachelorsRaw) && pct != null) {
        return `${percentBachelorsRaw.toFixed(1)}% of ${loc} householders aged 25 and older have a bachelor's degree or higher, ranking in the ${ordinal(pct)} percentile.`;
      }
      if (indicatorName === 'Health Insurance Coverage' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for health insurance coverage. Higher percentiles indicate a greater share of residents with coverage.`;
      }
      if (indicatorName === 'Income Equity by Race' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for income equity across racial/ethnic groups.`;
      }
      if (indicatorName === 'Educational Attainment Equity by Race' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for educational attainment equity across racial/ethnic groups.`;
      }
      if (indicatorName === 'Wage Equity by Sector' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for wage parity across economic sectors by race.`;
      }
      if (indicatorName === 'Median Household Income' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for median household income.`;
      }
      if (indicatorName === 'Job Wages' && pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for average wages across economic sectors.`;
      }
      if (pct != null) {
        return `${loc} ranks in the ${ordinal(pct)} percentile for ${indicatorName}.`;
      }
      return `${indicatorName}: no data for this location.`;
    })();

    if (calc) {
      return `${narrative} Calculation: ${calc}`;
    }
    return narrative;
  };

  /** Per-indicator descriptions for fallback/accessibility */
  const INDICATOR_INFO: Record<string, { description: string; calculation: string }> = Object.fromEntries(
    Object.entries(INDICATOR_CALCULATION).map(([k, v]) => [k, { description: k, calculation: v }])
  );

  // Placeholder data structure - values span full range to show all color quintiles
  // Category percentiles will be calculated as averages of their indicators
  // CES4 data is used for Pollution and Health indicators when available
  const baseCategories: Omit<Category, 'percentile'>[] = type === 'burdens' ? [
    {
      name: 'Greenhouse Gas Emissions (per capita)',
      expanded: false,
      indicators: [
        { name: 'Light-Duty Vehicles (LDV)', percentile: ghgPercentiles?.ldv ?? 10 },
        { name: 'Heavy-Duty Vehicles (HDV)', percentile: ghgPercentiles?.hdv ?? 18 },
        { name: 'Electricity', percentile: ghgPercentiles?.electricity ?? 12 },
        { name: 'Natural Gas', percentile: ghgPercentiles?.naturalGas ?? 16 },
        { name: 'Waste', percentile: ghgPercentiles?.waste ?? 14 },
        { name: 'Offroad', percentile: ghgPercentiles?.offroad ?? 19 }
      ]
    },
    {
      name: 'Pollution',
      expanded: false,
      indicators: [
        { name: 'Diesel PM', percentile: getIndicatorPercentile('Diesel PM', 38) },
        { name: 'PM2.5', percentile: getIndicatorPercentile('PM2.5', 32) },
        { name: 'Ozone', percentile: getIndicatorPercentile('Ozone', 36) },
        { name: 'Toxic Releases', percentile: getIndicatorPercentile('Toxic Releases', 34) },
        { name: 'Hazardous Waste', percentile: getIndicatorPercentile('Hazardous Waste', 33) }
      ]
    },
    {
      name: 'Health',
      expanded: false,
      indicators: [
        { name: 'Asthma Rate', percentile: getIndicatorPercentile('Asthma Rate', 52) },
        { name: 'Cardiovascular Disease', percentile: getIndicatorPercentile('Cardiovascular Disease', 55) },
        { name: 'Low Birth Weight', percentile: getIndicatorPercentile('Low Birth Weight', 54) }
      ]
    },
    {
      name: 'Transportation',
      expanded: false,
      indicators: [
        { name: 'Commute Time', percentile: commuteTimePercentile ?? null },
        { name: 'Traffic', percentile: getIndicatorPercentile('Traffic Density', 72) }
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Renter Burden', percentile: displayPercentile(housingBurdenRenterPercentile) },
        { name: 'Owner Burden', percentile: displayPercentile(housingBurdenOwnerPercentile) },
        { name: 'Displacement Risk', percentile: displacementRiskPercentile ?? null },
        { name: 'Energy Cost Burden', percentile: null }, // Coming soon
        {
          name: 'Percent Fossil Heating',
          percentile: displayPercentile(
            fossilHeatingLivabilityPercentile ?? fossilHeatingPercentile ?? percentFossilHeatingPercentile
          ),
        },
        { name: 'Urban Heat Island', percentile: urbanHeatIslandPercentile },
        { name: 'Crime and Safety Risks', percentile: crimeSafetyRiskPercentile != null ? Math.round(100 - crimeSafetyRiskPercentile) : null }
      ]
    }
  ] : [
    {
      name: 'Equity',
      expanded: false,
      indicators: [
        { name: 'Income Equity by Race', percentile: equityPercentiles?.incomeEquity ?? null },
        { name: 'Educational Attainment Equity by Race', percentile: equityPercentiles?.educationEquity ?? null },
        { name: 'Wage Equity by Sector', percentile: equityPercentiles?.wageEquity ?? null }
      ]
    },
    {
      name: 'Socioeconomic',
      expanded: false,
      indicators: [
        { name: 'Median Household Income', percentile: medianIncomePercentile ?? null },
        { name: 'Employment Rate', percentile: null }, // Coming soon
        { name: 'Educational Attainment', percentile: educationalAttainmentPercentile ?? null },
        { name: 'Health Insurance Coverage', percentile: healthInsurancePercentile ?? null },
        { name: 'Housing Affordability', percentile: null }, // Coming soon
        { name: 'Job Wages', percentile: jobWagePercentile ?? null }
      ]
    },
    {
      name: 'Mobility',
      expanded: false,
      indicators: [
        { name: 'Biking Infrastructure', percentile: null }, // Coming soon
        { name: 'Public Transit', percentile: null }, // Coming soon
        { name: "EV's per 1000 People", percentile: evPer1000Percentile ?? null },
        { name: 'EV Charging', percentile: evChargingPercentile ?? null },
        { name: 'Walkscore', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Affordable Housing', percentile: null }, // Coming soon
        { name: 'Solar PV', percentile: solarPVPercentile ?? null },
        { name: 'Heat Pump Adoption', percentile: heatPumpPercentile ?? null }
      ]
    },
    {
      name: 'Community Resources',
      expanded: false,
      indicators: [
        { name: 'Tree Canopy Coverage', percentile: treeCanopyPercentile ?? null },
        { name: 'Open Space Access', percentile: null }, // Coming soon
        { name: 'Public Safety', percentile: crimeSafetyRiskPercentile != null ? Math.round(100 - crimeSafetyRiskPercentile) : null }
      ]
    }
  ];

  // Calculate category percentiles as averages of their indicators
  const categories: Category[] = baseCategories.map(cat => ({
    ...cat,
    percentile: calculateCategoryPercentile(cat.indicators)
  }));

  // Calculate overall percentile as average of all categories and notify parent
  useEffect(() => {
    if (onPercentileCalculated && categories.length > 0) {
      const avg = categories.reduce((sum, cat) => sum + cat.percentile, 0) / categories.length;
      const overallPercentile = displayPercentile(avg);
      if (overallPercentile != null) onPercentileCalculated(overallPercentile);
    }
  }, [categories, onPercentileCalculated]);

  const handleCategoryClick = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    setExpandedCategories(new Set(categories.map(cat => cat.name)));
  };

  const handleCollapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Map indicator names to their corresponding routes
  const getIndicatorRoute = (indicatorName: string): string | null => {
    const routeMap: { [key: string]: string } = {
      // Burdens - Transportation
      'Light-Duty Vehicles (LDV)': '/transportation',
      'Heavy-Duty Vehicles (HDV)': '/transportation',
      'Commute Time': '/transportation',
      'Traffic': '/transportation',
      // Burdens - Buildings/Energy
      'Electricity': '/buildings',
      'Natural Gas': '/buildings',
      'Energy Cost Burden': '/buildings',
      'Percent Fossil Heating': '/buildings',
      // Burdens - GHG
      'Waste': '/other',
      'Offroad': '/ghg',
      // Burdens - Built Environment
      'Renter Burden': '/people',
      'Owner Burden': '/people',
      'Displacement Risk': '/people',
      // Burdens - Health
      'Asthma Rate': '/people',
      'Cardiovascular Disease': '/people',
      'Low Birth Weight': '/people',
      // Benefits - Transportation/Mobility
      "EV's per 1000 People": '/transportation',
      'EV Charging': '/transportation',
      'Public Transit': '/transportation',
      'Biking Infrastructure': '/transportation',
      // Benefits - Buildings
      'Solar PV': '/buildings',
      'Heat Pump Adoption': '/buildings',
      // Benefits - People/Socioeconomic
      'Income Equity by Race': '/people',
      'Educational Attainment Equity by Race': '/people',
      'Wage Equity by Sector': '/people',
      'Median Household Income': '/people',
      'Employment Rate': '/people',
      'Educational Attainment': '/people',
      'Health Insurance Coverage': '/people',
      'Housing Affordability': '/people',
      'Job Wages': '/people',
    };
    return routeMap[indicatorName] || null;
  };

  // Map indicator names to section anchor ids (for deep-linking to specific charts on target pages)
  const getIndicatorHash = (indicatorName: string): string | null => {
    const hashMap: { [key: string]: string } = {
      'Renter Burden': 'chart-housing-burden',
      'Owner Burden': 'chart-housing-burden',
      'Displacement Risk': 'chart-housing-burden',
      'Housing Affordability': 'chart-housing-burden',
      'Asthma Rate': 'chart-health',
      'Cardiovascular Disease': 'chart-health',
      'Low Birth Weight': 'chart-health',
      'Income Equity by Race': 'chart-income-poverty-by-race',
      'Median Household Income': 'chart-income-poverty-by-race',
      'Educational Attainment Equity by Race': 'chart-educational-attainment-by-race',
      'Educational Attainment': 'chart-educational-attainment-by-race',
      'Wage Equity by Sector': 'chart-job-wages',
      'Job Wages': 'chart-job-wages',
      'Health Insurance Coverage': 'chart-health',
      'Employment Rate': 'chart-employment',
      'Light-Duty Vehicles (LDV)': 'chart-vmt',
      'Heavy-Duty Vehicles (HDV)': 'chart-vmt',
      'Commute Time': 'chart-mode-share',
      'Traffic': 'chart-vmt',
      'Electricity': 'chart-electricity-consumption',
      'Natural Gas': 'chart-natural-gas-consumption',
      'Percent Fossil Heating': 'chart-house_heating_fuel',
      "EV's per 1000 People": 'chart-ev-adoption',
      'EV Charging': 'chart-ev-charging',
      'Public Transit': 'chart-mode-share',
      'Solar PV': 'chart-cumulative-solar',
    };
    return hashMap[indicatorName] || null;
  };

  const handleIndicatorClick = (indicatorName: string) => {
    const route = getIndicatorRoute(indicatorName);
    if (route) {
      const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
      const hash = getIndicatorHash(indicatorName);
      const path = slug ? `/${slug}${route}` : route;
      const fullPath = hash ? `${path}#${hash}` : path;
      navigate(fullPath);
    }
  };

  const getColor = (percentile: number) => {
    if (type === 'burdens') {
      // Burdens: Green (low) → Yellow → Red (high) - quintiles
      // Lower percentile = better (green), Higher percentile = worse (red)
      if (percentile < 20) return '#10b981';      // Green (0-20)
      if (percentile < 40) return '#34d399';     // Light Green (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#f59e0b';     // Orange (60-80)
      return '#dc2626';                           // Red (80-100)
    } else {
      // Benefits: Red (low) → Yellow → Green (high) - quintiles
      // Lower percentile = worse (red), Higher percentile = better (green)
      if (percentile < 20) return '#dc2626';      // Red (0-20)
      if (percentile < 40) return '#f59e0b';      // Orange (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#34d399';     // Light Green (60-80)
      return '#10b981';                           // Green (80-100)
    }
  };

  // Calculate total score as average of all category scores
  const totalScore = Math.round(
    categories.reduce((sum, cat) => sum + cat.percentile, 0) / categories.length
  );

  // Get label for percentile range
  const getLabel = (percentile: number) => {
    const rounded = Math.round(percentile);
    if (rounded < 20) return 'Very Low';
    if (rounded < 40) return 'Low';
    if (rounded >= 40 && rounded < 60) {
      // For average range, show "Less than Average" or "More than Average"
      // unless it's exactly 50% (rounded), which is just "Average"
      if (rounded === 50) return 'Average';
      return rounded < 50 ? 'Less than Average' : 'More than Average';
    }
    if (rounded < 80) return 'High';
    return 'Very High';
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* Total Score - Full Width */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Total Score:</span>
          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
            {totalScore}% ({getLabel(totalScore)})
          </span>
        </div>
        <div style={{ width: '100%', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
          <div
            style={{
              width: `${totalScore}%`,
              height: '100%',
              backgroundColor: getColor(totalScore),
              transition: 'width 0.3s ease'
            }}
          />
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
            {totalScore}%
          </span>
        </div>
        {/* Quintile Labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px' }}>
          <span style={{ color: type === 'burdens' ? '#10b981' : '#dc2626', fontWeight: '500' }}>Very Low</span>
          <span style={{ color: type === 'burdens' ? '#34d399' : '#f59e0b', fontWeight: '500' }}>Low</span>
          <span style={{ color: '#fbbf24', fontWeight: '500' }}>Average</span>
          <span style={{ color: type === 'burdens' ? '#f59e0b' : '#34d399', fontWeight: '500' }}>High</span>
          <span style={{ color: type === 'burdens' ? '#dc2626' : '#10b981', fontWeight: '500' }}>Very High</span>
        </div>
      </div>
      
      {/* Expand/Collapse Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={handleExpandAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#3b82f6',
            backgroundColor: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Expand
        </button>
        <button
          onClick={handleCollapseAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#6b7280',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Collapse
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.name);
          const indicatorList = category.indicators.map(indicator => indicator.name).join(', ');
          const categoryDescription = CATEGORY_INFO[type][category.name];
          const categoryTooltip = `${categoryDescription} Indicators: ${indicatorList}`;
          return (
            <div key={category.name} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
              <div
                onClick={() => handleCategoryClick(category.name)}
                title={categoryTooltip}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  backgroundColor: isExpanded ? '#f3f4f6' : 'transparent',
                  borderRadius: '4px'
                }}
              >
                <span style={{ fontWeight: '600', fontSize: '14px' }}>{category.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '200px', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${category.percentile}%`,
                        height: '100%',
                        backgroundColor: getColor(category.percentile),
                        transition: 'width 0.3s ease'
                      }}
                    />
                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                      {category.percentile}%
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </div>
              </div>
              {isExpanded && (
                <div style={{ marginTop: '12px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {category.indicators.map((indicator) => {
                      const hasData = indicator.percentile !== null;
                      const percentile = indicator.percentile as number | null;
                      const route = getIndicatorRoute(indicator.name);
                      const isClickable = route !== null;
                      const info = INDICATOR_INFO[indicator.name];
                      return (
                        <div key={indicator.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px', minHeight: 28 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, minWidth: 0 }}>
                            <span 
                              onClick={() => isClickable && handleIndicatorClick(indicator.name)}
                              style={{ 
                                fontSize: '12px', 
                                color: isClickable ? '#2563eb' : '#6b7280',
                                cursor: isClickable ? 'pointer' : 'default',
                                textDecoration: isClickable ? 'underline' : 'none',
                                textDecorationColor: isClickable ? '#2563eb' : 'transparent',
                                textUnderlineOffset: '2px'
                              }}
                              title={isClickable ? `Click to view ${indicator.name} chart` : undefined}
                            >
                              {indicator.name}
                            </span>
                            <span
                              className="indicator-info-wrap"
                              style={{ display: 'inline-flex', flexShrink: 0, position: 'relative' }}
                              role="img"
                              aria-label={info ? `Info: ${getSmartNarrative(indicator.name, category.name, percentile, selectedLocation)}` : 'Indicator info'}
                            >
                              <span
                                className="indicator-info-bubble"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 18,
                                  height: 18,
                                  minWidth: 18,
                                  minHeight: 18,
                                  borderRadius: '50%',
                                  backgroundColor: 'var(--chart-info-bubble-bg, #3b82f6)',
                                  color: '#ffffff',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: 'help',
                                  border: '1px solid var(--chart-info-bubble-border, #2563eb)',
                                  boxSizing: 'border-box'
                                }}
                              >
                                ℹ
                              </span>
                              {info && (
                                <span className="indicator-info-tooltip">
                                  {getSmartNarrative(indicator.name, category.name, percentile, selectedLocation)}
                                </span>
                              )}
                            </span>
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {hasData && percentile !== null ? (
                              <div style={{ width: '150px', height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentile}%`,
                                    height: '100%',
                                    backgroundColor: getColor(percentile),
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '10px', fontWeight: '600', color: '#374151' }}>
                                  {percentile}%
                                </span>
                              </div>
                            ) : (
                              <span style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>
                                Coming soon
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
